<template>
    <div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            <div class="col-span-1 md:col-span-2 h-full p-8 card">
                <LoyersAPercevoirTable v-if="loadedGlobalStatistiques" :cards="statistiquesCards" />
                <h3 class="text-sm font-semibold text-slate-800 dark:text-gray-100 mb-3 mt-5">Loyer à valider</h3>
                <NewLoyerList />
            </div>
            <div class="h-full p-8 card">
                <SkeletonLoader v-if="!loadedInfos" />
                <template v-else>
                    <h3 class="text-sm font-semibold text-slate-800 dark:text-gray-100 mb-3">Mes biens</h3>
                    <div class="flex flex-wrap justify-around items-center ">
                        <ul class="mt-4 lg:mt-0">
                            <li class="text-appartement">{{ infos.appartements }} appartement{{ infos.appartements > 1 ? 's' : '' }}</li>
                            <li class="text-maison">{{ infos.maisons }} maison{{ infos.maisons > 1 ? 's' : '' }}</li>
                            <li class="text-immeuble">{{ infos.immeubles }} immeuble{{ infos.immeubles > 1 ? 's' : '' }}</li>
                        </ul>
                        <div class="mt-4 mx-auto w-40 h-40 rounded-full" :style="chartClass"></div>
                    </div>
                </template>
            </div>
        </div>
        <div class="flex justify-end mt-5">
            <CheckboxForm fieldId="includeArchived" libelle="Afficher les biens archivés" :checked="includeArchived" @update:checked="onToggleIncludeArchived" />
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch mt-3">
            <template v-if="!loadedGlobalStatistiques">
                <div class="h-full p-8 card">
                    <SkeletonLoader />
                </div>
            </template>
            <template v-else>
                <div class="h-full p-8 card" v-for="card in statistiquesCards" :key="card.key">
                    <div class="flex justify-between items-baseline pb-3 mb-1 border-b border-gray-100 dark:border-gray-800">
                        <h3 class="text-sm font-semibold text-slate-800 dark:text-gray-100">{{ card.titre }}</h3>
                        <span class="text-base font-semibold" :class="card.statistiques.total_net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">{{ formatMoney(card.statistiques.total_net) }}</span>
                    </div>
                    <PretsProgression :prets-progress="card.statistiques.prets_progress" />
                    <GlobalTotalCumule
                        :loyers-total="card.statistiques.loyers_total"
                        :charges-total="card.statistiques.charges_total"
                        :taxe-fonciere-total="card.statistiques.taxe_fonciere_total"
                        :prets-total="card.statistiques.prets_total"
                        :montant-vente="card.statistiques.montant_vente"
                        :total-net="card.statistiques.total_net"
                    />
                    <BienTotauxTable :biens="card.statistiques.biens" />
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { ref, inject, onMounted, computed } from 'vue'
import SkeletonLoader from './../components/loader/SkeletonLoader';
import NewLoyerList from '../components/dashboard/new-loyer/NewLoyerList.vue';
import LoyersAPercevoirTable from '../components/dashboard/new-loyer/LoyersAPercevoirTable.vue';
import PretsProgression from '../components/bien/details/statistiques/PretsProgression.vue';
import GlobalTotalCumule from '../components/dashboard/statistiques/GlobalTotalCumule.vue';
import BienTotauxTable from '../components/dashboard/statistiques/BienTotauxTable.vue';
import CheckboxForm from '../components/form/CheckboxForm.vue';

export default {
    components: {
        SkeletonLoader,
        NewLoyerList,
        LoyersAPercevoirTable,
        PretsProgression,
        GlobalTotalCumule,
        BienTotauxTable,
        CheckboxForm
    },
    setup() {
        const loadedInfos = ref(false);
        const infos = ref([]);
        const chartClass = ref('');
        const loadedGlobalStatistiques = ref(false);
        const globalStatistiques = ref(null);
        const includeArchived = ref(false);
        const api = inject('api');

        const statistiquesCards = computed(() => {
            if (!globalStatistiques.value) {
                return [];
            }

            const cards = [];

            if (globalStatistiques.value.perso.biens.length > 0) {
                cards.push({key: 'perso', titre: 'Nom propre', statistiques: globalStatistiques.value.perso});
            }

            for (const sci of globalStatistiques.value.scis) {
                cards.push({key: sci.uuid, titre: sci.libelle, statistiques: sci.statistiques});
            }

            return cards;
        });

        onMounted(async () => {
            await loadInfos();
            await loadGlobalStatistiques();
        });

        async function getChartClass(infos) {
            const total = infos.appartements + infos.maisons + infos.immeubles;
            const appartementPercent = 100*infos.appartements/total;
            const maisonPercent = (100*infos.maisons/total)+appartementPercent;

            return `background: conic-gradient(#3b82f6 0% ${appartementPercent}%,#10b981 ${appartementPercent}% ${maisonPercent}%,#f59e0b ${maisonPercent}% 100%)`;

        }

        async function loadInfos() {
            loadedInfos.value = false

            infos.value = await api.dashboard.getInfos();
            chartClass.value = await getChartClass(infos.value);

            loadedInfos.value = true;
        }

        async function loadGlobalStatistiques() {
            loadedGlobalStatistiques.value = false;

            globalStatistiques.value = await api.dashboard.getGlobalStatistiques(includeArchived.value);

            loadedGlobalStatistiques.value = true;
        }

        async function onToggleIncludeArchived(checked) {
            includeArchived.value = checked;

            await loadGlobalStatistiques();
        }

        function formatMoney(value) {
            return `${Number(value || 0).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €`;
        }

        return {loadedInfos, infos, chartClass, loadedGlobalStatistiques, globalStatistiques, includeArchived, statistiquesCards, formatMoney, onToggleIncludeArchived}
    }
};
</script>